import React, { useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FileText, Check, PenTool, ChevronRight } from 'lucide-react';
import Header from '../components/Layout/Header';
import Modal from '../components/Modal';
import SignatureModal from '../components/SignatureModal';

const PageContainer = styled.div`
  flex: 1;
  background: ${({ theme }) => theme.colors.background};
  padding-bottom: 100px;
  overflow-y: auto;
`;

const Content = styled.div`
  padding: 20px;
`;

const FormCard = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const FormTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.primary};
`;

const FormDescription = styled.p`
  margin: 0 0 24px 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
  padding-bottom: 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const FormField = styled.div`
  margin-bottom: 24px;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 10px;

  span {
    color: ${({ theme }) => theme.colors.danger};
    margin-left: 4px;
  }
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  font-size: 15px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}20`};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const FieldTextarea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  font-size: 15px;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  resize: vertical;
  min-height: 120px;
  font-family: inherit;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}20`};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const OptionsGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: ${({ $selected, theme }) => $selected ? `${theme.colors.primary}10` : theme.colors.background};
  border: 1px solid ${({ $selected, theme }) => $selected ? theme.colors.primary : theme.colors.border};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => `${theme.colors.primary}08`};
  }

  input {
    width: 20px;
    height: 20px;
    accent-color: ${({ theme }) => theme.colors.primary};
  }

  span {
    font-size: 15px;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const SignatureBox = styled.div`
  width: 100%;
  min-height: 150px;
  border: 2px dashed ${({ $signed, theme }) => $signed ? theme.colors.primary : theme.colors.border};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: ${({ $signed, theme }) => $signed ? `${theme.colors.primary}05` : theme.colors.background};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
  padding: ${({ $signed }) => $signed ? '0' : '20px'};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  svg {
    width: 36px;
    height: 36px;
    opacity: ${({ $signed }) => $signed ? 1 : 0.5};
    color: ${({ $signed, theme }) => $signed ? theme.colors.primary : 'inherit'};
  }

  span {
    font-size: 14px;
    color: ${({ $signed, theme }) => $signed ? theme.colors.primary : 'inherit'};
    font-weight: ${({ $signed }) => $signed ? '500' : '400'};
  }
`;

const SignatureImage = styled.img`
  max-width: 100%;
  max-height: 140px;
  object-fit: contain;
`;

const SignatureLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary};
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const NotFoundMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.colors.textSecondary};

  svg {
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  h3 {
    font-size: 18px;
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.text};
    margin: 0 0 8px 0;
  }

  p {
    font-size: 14px;
    margin: 0;
  }
`;

// Formularios disponibles
const availableForms = [
  {
    id: 1,
    name: 'Consentimiento Informado',
    description: 'Formulario de consentimiento para procedimientos médicos',
    fields: [
      { id: 1, type: 'text', label: 'Nombre completo del paciente', required: true },
      { id: 2, type: 'text', label: 'Número de identificación', required: true },
      { id: 3, type: 'textarea', label: 'Descripción del procedimiento', required: true },
      { id: 4, type: 'multiple', label: '¿Ha sido informado de los riesgos?', options: ['Sí', 'No'], required: true },
      { id: 5, type: 'signature', label: 'Firma del paciente', required: true },
    ],
  },
  {
    id: 2,
    name: 'Historia Clínica Inicial',
    description: 'Formulario para recopilar información médica del paciente',
    fields: [
      { id: 1, type: 'text', label: 'Nombre completo', required: true },
      { id: 2, type: 'text', label: 'Fecha de nacimiento', required: true },
      { id: 3, type: 'textarea', label: 'Antecedentes médicos', required: false },
      { id: 4, type: 'textarea', label: 'Alergias conocidas', required: true },
      { id: 5, type: 'multiple', label: '¿Toma medicamentos actualmente?', options: ['Sí', 'No'], required: true },
      { id: 6, type: 'textarea', label: 'Lista de medicamentos', required: false },
    ],
  },
  {
    id: 3,
    name: 'Encuesta de Satisfacción',
    description: 'Formulario para evaluar la experiencia del paciente',
    fields: [
      { id: 1, type: 'multiple', label: '¿Cómo calificaría la atención recibida?', options: ['Excelente', 'Buena', 'Regular', 'Mala'], required: true },
      { id: 2, type: 'multiple', label: '¿Recomendaría nuestros servicios?', options: ['Definitivamente sí', 'Probablemente sí', 'Probablemente no', 'Definitivamente no'], required: true },
      { id: 3, type: 'textarea', label: 'Comentarios adicionales', required: false },
    ],
  }
];

const LlenarFormulario = () => {
  const { formId, patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const form = availableForms.find(f => f.id === parseInt(formId));
  const returnTo = location.state?.returnTo || `/perfil-paciente/${patientId}`;
  const editMode = location.state?.editMode || false;
  const existingValues = location.state?.existingValues || {};
  
  const [formValues, setFormValues] = useState(existingValues);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [currentSignatureField, setCurrentSignatureField] = useState(null);

  const handleInputChange = (fieldId, value) => {
    setFormValues(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleOpenSignature = (fieldId) => {
    setCurrentSignatureField(fieldId);
    setShowSignatureModal(true);
  };

  const handleSaveSignature = (signatureData) => {
    setFormValues(prev => ({
      ...prev,
      [currentSignatureField]: signatureData
    }));
    setCurrentSignatureField(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar campos requeridos
    const missingRequired = form.fields.filter(field => 
      field.required && !formValues[field.id]
    );

    if (missingRequired.length > 0) {
      return;
    }

    // Guardar formulario (en una app real, enviaría a backend)
    console.log('Formulario guardado:', {
      formId,
      patientId,
      values: formValues,
      submittedAt: new Date().toISOString()
    });

    setShowSuccessModal(true);
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'text':
        return (
          <FieldInput 
            type="text" 
            placeholder={`Ingrese ${field.label.toLowerCase()}`}
            value={formValues[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
          />
        );
      case 'textarea':
        return (
          <FieldTextarea 
            placeholder={`Ingrese ${field.label.toLowerCase()}`}
            value={formValues[field.id] || ''}
            onChange={(e) => handleInputChange(field.id, e.target.value)}
          />
        );
      case 'multiple':
        return (
          <OptionsGroup>
            {field.options?.map((option, idx) => (
              <OptionLabel 
                key={idx}
                $selected={formValues[field.id] === option}
              >
                <input 
                  type="radio" 
                  name={`field-${field.id}`}
                  checked={formValues[field.id] === option}
                  onChange={() => handleInputChange(field.id, option)}
                />
                <span>{option}</span>
              </OptionLabel>
            ))}
          </OptionsGroup>
        );
      case 'signature':
        const signatureValue = formValues[field.id];
        const isSigned = signatureValue && signatureValue !== 'signed' && signatureValue.startsWith('data:');
        const isOldSigned = signatureValue === 'signed';
        
        return (
          <SignatureBox 
            $signed={isSigned || isOldSigned}
            onClick={() => handleOpenSignature(field.id)}
          >
            {isSigned ? (
              <>
                <SignatureImage src={signatureValue} alt="Firma" />
                <SignatureLabel>
                  <Check size={14} />
                  Toque para editar firma
                </SignatureLabel>
              </>
            ) : isOldSigned ? (
              <>
                <Check />
                <span>Firmado ✓</span>
                <SignatureLabel>Toque para editar firma</SignatureLabel>
              </>
            ) : (
              <>
                <PenTool />
                <span>Toque aquí para firmar</span>
              </>
            )}
          </SignatureBox>
        );
      default:
        return null;
    }
  };

  if (!form) {
    return (
      <PageContainer>
        <Header title={editMode ? "Editar Formulario" : "Llenar Formulario"} showBack />
        <Content>
          <NotFoundMessage>
            <FileText />
            <h3>Formulario no encontrado</h3>
            <p>El formulario que buscas no existe</p>
          </NotFoundMessage>
        </Content>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header title={editMode ? "Editar Formulario" : "Llenar Formulario"} showBack />
      
      <Content>
        <FormCard>
          <FormTitle>{form.name}</FormTitle>
          <FormDescription>{form.description}</FormDescription>
          
          <form onSubmit={handleSubmit}>
            {form.fields.map((field) => (
              <FormField key={field.id}>
                <FieldLabel>
                  {field.label}
                  {field.required && <span>*</span>}
                </FieldLabel>
                {renderField(field)}
              </FormField>
            ))}

            <SubmitButton type="submit">
              <Check size={20} />
              {editMode ? 'Actualizar Formulario' : 'Guardar Formulario'}
            </SubmitButton>
          </form>
        </FormCard>
      </Content>

      <Modal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          navigate(returnTo);
        }}
        type="success"
        title={editMode ? "Formulario Actualizado" : "Formulario Guardado"}
        message={editMode ? "El formulario se ha actualizado correctamente." : "El formulario se ha guardado correctamente."}
        confirmText="Aceptar"
        onConfirm={() => {
          setShowSuccessModal(false);
          navigate(returnTo);
        }}
      />

      <SignatureModal
        isOpen={showSignatureModal}
        onClose={() => {
          setShowSignatureModal(false);
          setCurrentSignatureField(null);
        }}
        onSave={handleSaveSignature}
        existingSignature={currentSignatureField ? formValues[currentSignatureField] : null}
      />
    </PageContainer>
  );
};

export default LlenarFormulario;
